import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiAward,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiInfo,
  FiMessageSquare,
  FiStar,
  FiX,
} from "react-icons/fi";
import AttendanceCard from "../components/AttendanceCard";
import ComponentLoader from "../components/ComponentLoader";
import TimeEntriesPanel from "../components/TimeEntriesPanel";
import { getAttendanceMonthRows, isActionableUnknownAttendanceRow } from "./LeaveScreen";
import ScreenHeader from "./ScreenHeader";

const getUpcomingHoliday = (holidays = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return holidays.find((holiday) => {
    const rawDate = holiday.dateKey || holiday.rawDate || holiday.date;
    const holidayDate = new Date(String(rawDate).includes("T") ? rawDate : `${rawDate}T00:00:00`);
    return !Number.isNaN(holidayDate.getTime()) && holidayDate >= today;
  });
};

const toDateKey = (value) => {
  if (!value) {
    return "";
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return "";
    }
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }
  const rawValue = String(value).trim();
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }
  const date = new Date(rawValue.includes("T") ? rawValue : `${rawValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const getMonthBounds = (monthKey) => {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  const baseDate = Number.isFinite(year) && Number.isFinite(month)
    ? new Date(year, month - 1, 1)
    : new Date();
  return {
    startKey: toDateKey(baseDate),
    endKey: toDateKey(new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0)),
  };
};

const getCurrentMonthKey = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

export const DASHBOARD_ANNOUNCEMENTS = [];

export const POPUP_ANNOUNCEMENTS = DASHBOARD_ANNOUNCEMENTS;

export const getTodaysAnnouncements = (announcements = DASHBOARD_ANNOUNCEMENTS, date = new Date()) => {
  const todayKey = toDateKey(date);
  return announcements.filter((announcement) => announcement.dateKey === todayKey);
};

export const getAnnouncementSpotlight = (announcements = DASHBOARD_ANNOUNCEMENTS) =>
  announcements.find((announcement) => announcement.spotlight) || announcements[0] || null;

export const getAnnouncementSpotlightIndex = (announcements = DASHBOARD_ANNOUNCEMENTS) => {
  const spotlightIndex = announcements.findIndex((announcement) => announcement.spotlight);
  return spotlightIndex >= 0 ? spotlightIndex : 0;
};

export const getNextAnnouncementIndex = (currentIndex, direction, announcements = DASHBOARD_ANNOUNCEMENTS) => {
  const count = announcements.length;
  if (!count) {
    return 0;
  }
  return (currentIndex + direction + count) % count;
};

const isApprovedStatus = (status) => String(status || "").toLowerCase().includes("approved");

const isLossOfPayLeave = (request) => {
  const leaveType = String(request?.leaveType || request?.type || "").trim().toLowerCase();
  return leaveType === "lop" || leaveType.includes("loss of pay");
};

const getNextDateKey = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setDate(date.getDate() + 1);
  return toDateKey(date);
};

const countWorkingDays = (startKey, endKey, holidayKeys) => {
  let total = 0;
  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
    const date = new Date(`${dateKey}T00:00:00`);
    const day = date.getDay();
    if (day !== 0 && day !== 6 && !holidayKeys.has(dateKey)) {
      total += 1;
    }
  }
  return total;
};

const calculateApprovedLopDaysForMonth = (leaveRequests = [], holidays = [], monthKey = getCurrentMonthKey()) => {
  const { startKey: monthStartKey, endKey: monthEndKey } = getMonthBounds(monthKey);
  const holidayKeys = new Set(holidays.map((holiday) => toDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.date)).filter(Boolean));

  return leaveRequests.reduce((total, request) => {
    if (!isApprovedStatus(request?.status) || !isLossOfPayLeave(request)) {
      return total;
    }

    const requestStartKey = toDateKey(request.startDateKey || request.startDate || request.date);
    const requestEndKey = toDateKey(request.endDateKey || request.endDate || request.date || requestStartKey) || requestStartKey;
    if (!requestStartKey || !requestEndKey || requestStartKey > monthEndKey || requestEndKey < monthStartKey) {
      return total;
    }

    const leaveDays = Number(request.leaveDays || request.days || 0);
    if (leaveDays === 0.5 && requestStartKey >= monthStartKey && requestStartKey <= monthEndKey) {
      return total + 0.5;
    }

    const overlapStartKey = requestStartKey > monthStartKey ? requestStartKey : monthStartKey;
    const overlapEndKey = requestEndKey < monthEndKey ? requestEndKey : monthEndKey;
    const overlapWorkingDays = countWorkingDays(overlapStartKey, overlapEndKey, holidayKeys);
    const requestWorkingDays = countWorkingDays(requestStartKey, requestEndKey, holidayKeys);
    const cappedDays = leaveDays > 0 && requestWorkingDays > 0
      ? Math.min(overlapWorkingDays, leaveDays)
      : overlapWorkingDays;

    return total + cappedDays;
  }, 0);
};

const formatLeaveDays = (days) => {
  const rounded = Number(Number(days || 0).toFixed(2));
  return `${rounded} ${rounded === 1 ? "day" : "days"}`;
};

export const isClosedCaseStatus = (status = "") => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus === "closed" || normalizedStatus === "resolved" || normalizedStatus.startsWith("closed ");
};

export const countOpenCases = (requests = []) =>
  requests.filter((request) => !isClosedCaseStatus(request?.status)).length;

const compactDefaulterReason = (flag = {}) => {
  const count = Number(flag.count || 0);
  if (flag.type === "lopRisk") {
    return `${count} LOP ${count === 1 ? "day" : "days"}`;
  }
  if (flag.type === "attendance") {
    return `${count} unknown days`;
  }
  if (flag.type === "policy") {
    return `${count} policy reminders`;
  }
  if (flag.type === "criticalTasks") {
    return `${count} critical ${count === 1 ? "reminder" : "reminders"}`;
  }
  if (flag.type === "timecards") {
    return `${count} unfilled days`;
  }
  return flag.label || "Needs action";
};

const AnnouncementCarousel = ({
  activeIndex,
  announcements = DASHBOARD_ANNOUNCEMENTS,
  label = "Session announcements",
  onNext,
  onPrevious,
  onSelect,
  variant = "dashboard",
}) => {
  if (!announcements.length) {
    return null;
  }

  const safeIndex = Math.min(Math.max(activeIndex, 0), announcements.length - 1);
  const announcement = announcements[safeIndex];
  const isPopup = variant === "popup";

  return (
    <div className={`new-hrm-announcement-carousel new-hrm-announcement-carousel--${variant}`} aria-label={label}>
      <article
        className={[
          "new-hrm-announcement-card",
          isPopup ? "new-hrm-announcement-card--spotlight" : "",
          announcement.completed ? "new-hrm-announcement-card--completed" : "",
          `new-hrm-announcement-card--${announcement.tone}`,
        ].filter(Boolean).join(" ")}
      >
        <span className="new-hrm-announcement-card__avatar" aria-hidden="true">
          {announcement.imageUrl ? <img src={announcement.imageUrl} alt="" /> : announcement.speaker.slice(0, 1)}
        </span>
        <div>
          <small>{announcement.badge}</small>
          {announcement.completed && <span className="new-hrm-announcement-card__thanks">{announcement.thankYouLabel || "Thank you"}</span>}
          <strong>{announcement.title}</strong>
          <em>{announcement.speaker} - {announcement.subtitle}</em>
          {announcement.appreciationText && <span>{announcement.appreciationText}</span>}
          {isPopup && <span>{announcement.dateLabel} | {announcement.timeLabel}</span>}
        </div>
        {!isPopup && (
          <time dateTime={announcement.dateKey}>
            {announcement.timing}
            <span>{announcement.dateLabel}</span>
            <span>{announcement.timeLabel}</span>
          </time>
        )}
      </article>
      <div className="new-hrm-announcement-carousel__controls">
        <button type="button" onClick={onPrevious} aria-label="Previous announcement">
          <FiChevronLeft aria-hidden="true" />
        </button>
        <div className="new-hrm-announcement-carousel__dots" role="tablist" aria-label="Announcement slides">
          {announcements.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={index === safeIndex ? "is-active" : undefined}
              onClick={() => onSelect(index)}
              aria-label={`Show ${item.speaker} announcement`}
              aria-selected={index === safeIndex}
              role="tab"
            />
          ))}
        </div>
        <button type="button" onClick={onNext} aria-label="Next announcement">
          <FiChevronRight aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const DashboardScreen = ({
  attendance,
  attendanceMarkingAllowed = false,
  attendanceNetworkMessage = "",
  attendanceNetworkResolved = false,
  attendanceLoading = false,
  showAttendanceWarning = true,
  dataError,
  dataReady = false,
  dataLoading,
  dataLoadingLabel = "Refreshing HRM data",
  defaulterLeaderboard,
  holidays,
  isTimesheetLocked,
  leaveBalances,
  leaveRequests,
  monthTimesheet,
  onAction,
  onAttendanceToggle,
  onOpenAttendanceHistory,
  onOpenLeaveTab,
  onOpenTimeEntries,
  onRefreshTimesheet,
  onSubmitTime,
  onSubmitTimesheetApproval,
  onTimeCellEdit,
  onTimesheetCellToggle,
  onTimesheetDateSelect,
  onTimesheetMonthChange,
  selectedTimesheetCellIds,
  selectedTimesheetCellCount,
  selectedTimesheetDate,
  selectedTimesheetMonth,
  selectedTimesheetMonthId,
  projectOptions = [],
  taskOptions = [],
  timesheetMonths,
  requests = [],
  timeLoading = false,
  timeLoadingLabel = "Saving timesheet",
  timesheetLockMessage,
  totalHours,
}) => {
  const [showAnnouncementPopup, setShowAnnouncementPopup] = useState(false);
  const [popupAnnouncementIndex, setPopupAnnouncementIndex] = useState(() => getAnnouncementSpotlightIndex(POPUP_ANNOUNCEMENTS));
  const [dashboardAnnouncementIndex, setDashboardAnnouncementIndex] = useState(() => getAnnouncementSpotlightIndex(DASHBOARD_ANNOUNCEMENTS));
  const totalRemainingLeave = leaveBalances.reduce((sum, balance) => sum + Number(balance.remaining || 0), 0);
  const pendingLeaves = leaveRequests.filter((request) => request.status === "Pending").length;
  const openRequests = countOpenCases(requests);
  const nextHoliday = getUpcomingHoliday(holidays);
  const approvedLopDays = calculateApprovedLopDaysForMonth(leaveRequests, holidays);
  const leaderboardRows = Array.isArray(defaulterLeaderboard?.defaulters) ? defaulterLeaderboard.defaulters : [];
  const hasDefaulters = leaderboardRows.length > 0;
  const todayKey = toDateKey(new Date());
  const attendanceDateKey = toDateKey(attendance?.rawLoggedDate || attendance?.loggedDate || attendance?.date);
  const isPresentToday =
    attendanceDateKey === todayKey &&
    Boolean(attendance?.status === "checked-in" || attendance?.checkInAt || attendance?.checkInTime);
  const hasCurrentAttendanceLog = (attendance?.logs || []).some((log) => {
    const logDateKey = toDateKey(log.rawLoggedDate || log.loggedDate || log.date || log.dateKey);
    return logDateKey && logDateKey === todayKey;
  });
  const dashboardAttendanceLogs =
    isPresentToday && !hasCurrentAttendanceLog
      ? [
          {
            id: "active-attendance-log",
            rawLoggedDate: attendance?.rawLoggedDate || new Date(),
            loggedDate: attendance?.loggedDate,
            status: "Present",
          },
          ...(attendance?.logs || []),
        ]
      : attendance?.logs || [];
  const currentMonthAttendanceRows = getAttendanceMonthRows(dashboardAttendanceLogs, leaveRequests, holidays, getCurrentMonthKey());
  const unknownAttendanceRows = dataReady ? currentMonthAttendanceRows.filter((row) => isActionableUnknownAttendanceRow(row, todayKey)) : [];
  const hasUnknownAttendance = unknownAttendanceRows.length > 0;
  const dashboardDate = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const spotlightAnnouncement = useMemo(() => getAnnouncementSpotlight(POPUP_ANNOUNCEMENTS), []);
  const hasSpotlightAnnouncement = Boolean(spotlightAnnouncement);

  useEffect(() => {
    if (!hasSpotlightAnnouncement) {
      return;
    }

    setShowAnnouncementPopup(true);
  }, [hasSpotlightAnnouncement]);

  const closeAnnouncementPopup = () => {
    setShowAnnouncementPopup(false);
  };

  const openAnnouncementPopup = () => {
    if (!POPUP_ANNOUNCEMENTS.length) {
      return;
    }

    setPopupAnnouncementIndex(getAnnouncementSpotlightIndex(POPUP_ANNOUNCEMENTS));
    setShowAnnouncementPopup(true);
  };

  const showPreviousPopupAnnouncement = () => {
    setPopupAnnouncementIndex((currentIndex) => getNextAnnouncementIndex(currentIndex, -1, POPUP_ANNOUNCEMENTS));
  };

  const showNextPopupAnnouncement = () => {
    setPopupAnnouncementIndex((currentIndex) => getNextAnnouncementIndex(currentIndex, 1, POPUP_ANNOUNCEMENTS));
  };

  const showPreviousDashboardAnnouncement = () => {
    setDashboardAnnouncementIndex((currentIndex) => getNextAnnouncementIndex(currentIndex, -1, DASHBOARD_ANNOUNCEMENTS));
  };

  const showNextDashboardAnnouncement = () => {
    setDashboardAnnouncementIndex((currentIndex) => getNextAnnouncementIndex(currentIndex, 1, DASHBOARD_ANNOUNCEMENTS));
  };

  const summaryItems = [
    {
      id: "time",
      label: "Month hours",
      value: totalHours,
      detail: "Logged",
      icon: FiClock,
      tone: "blue",
      action: () => onAction("timesheet"),
    },
    {
      id: "leave",
      label: "Leave balance",
      value: leaveBalances.length ? `${totalRemainingLeave} days` : "--",
      detail: "Remaining",
      icon: FiCalendar,
      tone: "green",
      action: onOpenLeaveTab,
    },
    {
      id: "requests",
      label: "Pending leaves",
      value: pendingLeaves,
      detail: "Awaiting approval",
      icon: FiFileText,
      tone: "purple",
      action: onOpenLeaveTab,
    },
    {
      id: "lop",
      label: "Approved LOP",
      value: formatLeaveDays(approvedLopDays),
      detail: "This month deduction",
      icon: FiAlertTriangle,
      tone: "red",
      action: onOpenLeaveTab,
    },
    {
      id: "cases",
      label: "Open cases",
      value: openRequests,
      detail: "Active requests",
      icon: FiMessageSquare,
      tone: "amber",
      action: () => onAction("hr"),
    },
  ];

  return (
    <div
      className={[
        "new-hrm-dashboard-overview",
        hasUnknownAttendance ? "new-hrm-dashboard-danger-mode" : "",
      ].filter(Boolean).join(" ")}
    >
      <ScreenHeader
        eyebrow="Employee workspace"
        title="Dashboard"
        description={dashboardDate}
      />
      {showAnnouncementPopup && hasSpotlightAnnouncement && (
        <div className="new-hrm-modal-backdrop new-hrm-announcement-popup-backdrop" role="presentation" onClick={closeAnnouncementPopup}>
          <section
            className="new-hrm-modal new-hrm-announcement-popup"
            role="dialog"
            aria-modal="true"
            aria-label="Employee-led learning announcements"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="new-hrm-icon-button new-hrm-announcement-popup__close"
              onClick={closeAnnouncementPopup}
              aria-label="Close announcement"
            >
              <FiX aria-hidden="true" />
            </button>
            <div className="new-hrm-announcement-popup__hero">
              <span className="new-hrm-announcement-popup__logo">
                <img src="/assets/antrazal-brand.svg" alt="Antrazal" />
              </span>
              <div className="new-hrm-announcement-popup__spotlight">
                <AnnouncementCarousel
                  announcements={POPUP_ANNOUNCEMENTS}
                  activeIndex={popupAnnouncementIndex}
                  label="Popup session announcements"
                  onNext={showNextPopupAnnouncement}
                  onPrevious={showPreviousPopupAnnouncement}
                  onSelect={setPopupAnnouncementIndex}
                  variant="popup"
                />
              </div>
            </div>
          </section>
        </div>
      )}
      {dataError && (
        <div className="new-hrm-data-banner new-hrm-data-banner--error">
          {dataError}
        </div>
      )}
      <AttendanceCard
        attendance={attendance}
        attendanceMarkingAllowed={attendanceMarkingAllowed}
        attendanceNetworkMessage={attendanceNetworkMessage}
        attendanceNetworkResolved={attendanceNetworkResolved}
        loading={attendanceLoading || dataLoading}
        loadingLabel={attendanceLoading ? "Saving attendance" : dataLoadingLabel}
        onToggle={onAttendanceToggle}
        onViewMore={onOpenAttendanceHistory}
        unknownAttendanceRows={showAttendanceWarning ? unknownAttendanceRows : []}
      />

      <section className={`new-hrm-dashboard-priority-grid ${hasDefaulters ? "" : "new-hrm-dashboard-priority-grid--no-defaulters"}`}>
        <section className="new-hrm-dashboard-summary new-hrm-dashboard-kpi-band">
          <div className="new-hrm-dashboard-summary__cards">
            {summaryItems.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.id} type="button" className="new-hrm-dashboard-kpi" onClick={item.action}>
                  <span className={`new-hrm-dashboard-kpi__icon new-hrm-tone--${item.tone}`}>
                    <Icon aria-hidden="true" />
                  </span>
                  <span>
                    <small>{item.label}</small>
                    <strong>{item.value}</strong>
                    <em>{item.detail}</em>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {hasDefaulters && (
          <section className="new-hrm-defaulter-alert new-hrm-defaulter-alert--dashboard" aria-label="HRM Defaulter leaderboard">
            <header>
              <span className="new-hrm-defaulter-alert__star" aria-hidden="true">
                <FiStar />
              </span>
              <div>
                <small>Leaderboard</small>
                <strong>{defaulterLeaderboard?.title || "HRM Defaulter"}</strong>
              </div>
              <span className="new-hrm-defaulter-alert__info">
                <button
                  type="button"
                  aria-describedby="defaulter-info-note"
                  aria-label="How to become an HRM defaulter"
                  title="How to become an HRM defaulter"
                >
                  <FiInfo aria-hidden="true" />
                </button>
                <span id="defaulter-info-note" role="tooltip">
                  Want this badge too? Easy. Leave timecards unsubmitted for 5+ working days, ignore 3 policy reminders,
                  let a critical task survive 3 reminders, or collect 2 unknown attendance days. A clean little shortcut
                  to HRM fame.
                </span>
              </span>
            </header>
            <ul>
              {leaderboardRows.map((row) => {
                const flags = Array.isArray(row.flags) ? row.flags : [];
                return (
                  <li key={row.contactId || row.email || row.name}>
                    <span>{row.name}</span>
                    <div className="new-hrm-defaulter-alert__reasons">
                      {flags.map((flag, index) => (
                        <em
                          key={`${flag.type || "flag"}-${index}`}
                          className={flag.type === "lopRisk" ? "new-hrm-defaulter-alert__reason--star" : undefined}
                        >
                          {flag.type === "lopRisk" && <FiStar aria-hidden="true" />}
                          {compactDefaulterReason(flag)}
                        </em>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </section>

      <div className="new-hrm-dashboard-work-grid new-hrm-dashboard-work-grid--clean">
        <TimeEntriesPanel
          holidays={holidays}
          isTimesheetLocked={isTimesheetLocked}
          loading={dataLoading || timeLoading}
          loadingLabel={timeLoading ? timeLoadingLabel : dataLoadingLabel}
          monthTimesheet={monthTimesheet}
          onAction={onAction}
          onExpand={onOpenTimeEntries}
          onRefreshTimesheet={onRefreshTimesheet}
          onSubmitTime={onSubmitTime}
          onSubmitTimesheetApproval={onSubmitTimesheetApproval}
          onTimeCellEdit={onTimeCellEdit}
          onTimesheetCellToggle={onTimesheetCellToggle}
          onTimesheetDateSelect={onTimesheetDateSelect}
          onTimesheetMonthChange={onTimesheetMonthChange}
          selectedTimesheetCellIds={selectedTimesheetCellIds}
          selectedTimesheetCellCount={selectedTimesheetCellCount}
          selectedTimesheetDate={selectedTimesheetDate}
          selectedTimesheetMonth={selectedTimesheetMonth}
          selectedTimesheetMonthId={selectedTimesheetMonthId}
          projectOptions={projectOptions}
          taskOptions={taskOptions}
          timesheetMonths={timesheetMonths}
          timesheetLockMessage={timesheetLockMessage}
          totalHours={totalHours}
        />
        <aside className="new-hrm-dashboard-side-stack">
          {DASHBOARD_ANNOUNCEMENTS.length > 0 && (
            <section className="new-hrm-panel new-hrm-dashboard-announcements">
              <header className="new-hrm-panel__header new-hrm-panel__header--compact">
                <div>
                  <h2>Announcements</h2>
                  <p>Employee-led learning sessions</p>
                </div>
                <div className="new-hrm-dashboard-announcements__actions">
                  <button type="button" className="new-hrm-link-button" onClick={openAnnouncementPopup}>
                    View announcements
                  </button>
                  <span className="new-hrm-dashboard-announcements__icon" aria-hidden="true">
                    <FiAward />
                  </span>
                </div>
              </header>
              <AnnouncementCarousel
                activeIndex={dashboardAnnouncementIndex}
                label="Dashboard session announcements"
                onNext={showNextDashboardAnnouncement}
                onPrevious={showPreviousDashboardAnnouncement}
                onSelect={setDashboardAnnouncementIndex}
                variant="dashboard"
              />
            </section>
          )}
          <section className="new-hrm-panel new-hrm-dashboard-today new-hrm-loader-host">
            <ComponentLoader active={dataLoading} label={dataLoadingLabel} />
            <header className="new-hrm-panel__header new-hrm-panel__header--compact">
              <h2>Today</h2>
            </header>
            <div className="new-hrm-dashboard-today__list">
              <button type="button" onClick={onOpenLeaveTab}>
                <span>Next holiday</span>
                <strong>{nextHoliday?.name || "No holiday found"}</strong>
                <small>{nextHoliday?.date || "--"}</small>
              </button>
              <button type="button" onClick={onOpenLeaveTab}>
                <span>Leave requests</span>
                <strong>{pendingLeaves ? `${pendingLeaves} pending` : "Nothing pending"}</strong>
                <small>{leaveRequests[0]?.requestNumber || "Latest status clear"}</small>
              </button>
              <button type="button" onClick={() => onAction("hr")}>
                <span>Open cases</span>
                <strong>{openRequests ? `${openRequests} open` : "No open cases"}</strong>
                <small>{requests[0]?.requestNumber || "Request queue clear"}</small>
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default DashboardScreen;
